<template>
  <div class="p-2 d-flex flex-wrap gap-2">
    <button
      v-if="showDeleteActions"
      class="btn btn-outline-danger"
      :disabled="!selectedIds.length"
      @click="bulkDelete"
    >
      Delete selected ({{ selectedIds.length }})
    </button>
    <button
      v-if="showApproveActions"
      class="btn btn-outline-success"
      :disabled="!selectedIds.length"
      @click="bulkApprove('Approved')"
    >
      Approve selected ({{ selectedIds.length }})
    </button>
    <button
      v-if="showApproveActions"
      class="btn btn-outline-warning"
      :disabled="!selectedIds.length"
      @click="bulkApprove('Rejected')"
    >
      Reject selected ({{ selectedIds.length }})
    </button>
  </div>

  <a-table
    class="table datatable thead-light"
    :columns="columns"
    :data-source="data"
    :loading="loading"
    :row-selection="rowSelection"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Employee'">
        <div class="d-flex align-items-center file-name-icon">
          <a
            href="javascript:void(0);"
            class="avatar avatar-md border avatar-rounded"
          >
            <img :src="record.image" class="img-fluid" alt="img" />
          </a>
          <div class="ms-2">
            <h6 class="fw-medium">
              <a href="javascript:void(0);">{{
                record.employee_name || "Employee"
              }}</a>
            </h6>
            <span class="fs-12 fw-normal">{{ record.role }}</span>
          </div>
        </div>
      </template>

      <template v-if="column.key === 'Project'">
        <p class="fs-14 fw-medium text-gray-9 d-flex align-items-center">
          {{ record.project }}
        </p>
      </template>

      <template v-if="column.key === 'Status'">
        <span class="badge" :class="statusClass(record.status)">{{
          record.status || "Draft"
        }}</span>
      </template>

      <template v-if="column.key === 'Date'">
        <p class="fs-14 fw-medium text-gray-9 d-flex align-items-center">
          {{ formatDate(record) }}
        </p>
      </template>

      <template v-if="column.key === 'action'">
        <div class="action-icon d-inline-flex align-items-center gap-1">
          <a
            v-if="showApproveActions && canReview(record.status)"
            href="javascript:void(0);"
            class="me-1 text-success"
            title="Approve"
            @click="$emit('approve', record._raw, 'Approved')"
            ><i class="ti ti-check"></i
          ></a>
          <a
            v-if="showApproveActions && canReview(record.status)"
            href="javascript:void(0);"
            class="me-1 text-warning"
            title="Reject"
            @click="$emit('approve', record._raw, 'Rejected')"
            ><i class="ti ti-x"></i
          ></a>
          <a
            v-if="showEditActions && canEdit(record.status)"
            href="javascript:void(0);"
            class="me-2"
            @click="$emit('edit', record._raw)"
            v-can="'TimingSheets.Edit'"
            ><i class="ti ti-edit"></i
          ></a>
          <a
            v-if="showDeleteActions && canEdit(record.status)"
            href="javascript:void(0);"
            @click="$emit('delete', record._raw)"
            v-can="'TimingSheets.Delete'"
            ><i class="ti ti-trash"></i
          ></a>
        </div>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { computed, ref } from "vue";

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  showDeleteActions: { type: Boolean, default: true },
  showEditActions: { type: Boolean, default: true },
  showApproveActions: { type: Boolean, default: false },
});
const emit = defineEmits(["edit", "delete", "bulk-delete", "approve", "bulk-approve"]);

const selectedIds = ref([]);

const columns = [
  { sorter: false },
  {
    title: "Employee",
    dataIndex: "employee_name",
    key: "Employee",
    sorter: true,
  },
  { title: "Date", dataIndex: "work_date", key: "Date", sorter: true },
  { title: "Project", dataIndex: "project", key: "Project", sorter: true },
  {
    title: "Assigned Hours",
    dataIndex: "total_hours",
    key: "Assigned_Hours",
    sorter: true,
  },
  {
    title: "Worked Hours",
    dataIndex: "worked_hours",
    key: "Worked_Hours",
    sorter: true,
  },
  { title: "Status", dataIndex: "status", key: "Status", sorter: true },
  { title: "", key: "action", sorter: false },
];

const data = computed(() =>
  (props.rows || []).map((r) => ({
    key: r.id,
    _raw: r,
    employee_name: `${
      r?.employee?.user?.first_name ?? r?.employee?.first_name ?? ""
    } ${r?.employee?.user?.last_name ?? r?.employee?.last_name ?? ""}`.trim(),
    work_date: r.work_date,
    project: r.project,
    status: r.status,
    total_hours: Number(r.total_hours ?? 0).toFixed(2),
    worked_hours: Number(r.worked_hours ?? 0).toFixed(2),
    image: r?.employee?.avatar_url || "user-01.jpg",
    role: r?.employee?.department?.name || "",
  }))
);

function onRowSelect(checked, record) {
  const id = record?._raw?.id;
  if (!id) return;
  if (checked) {
    if (!selectedIds.value.includes(id)) selectedIds.value.push(id);
  } else {
    selectedIds.value = selectedIds.value.filter((x) => x !== id);
  }
}

function onSelectAll(checked) {
  selectedIds.value = checked ? data.value.map((d) => d._raw.id) : [];
}

function bulkDelete() {
  if (!selectedIds.value.length) return;
  emit("bulk-delete", selectedIds.value.slice());
  selectedIds.value = [];
}

function bulkApprove(status) {
  if (!selectedIds.value.length) return;
  emit("bulk-approve", selectedIds.value.slice(), status);
  selectedIds.value = [];
}

const rowSelection = {
  onChange: () => {},
  onSelect: onRowSelect,
  onSelectAll,
};

function formatDate(r) {
  const s = r?.work_date ? new Date(r.work_date).toLocaleDateString() : "-";
  return `${s}`;
}

function statusClass(status) {
  if (status === "Approved") return "bg-success";
  if (status === "Rejected") return "bg-danger";
  if (status === "Submitted") return "bg-warning text-dark";
  return "bg-secondary";
}

function canReview(status) {
  return ["Submitted", "Draft"].includes(status);
}

function canEdit(status) {
  return !["Approved", "Rejected"].includes(status);
}
</script>
